import * as THREE from 'three';
import { OrbitControls } from 'src/lib/OrbitControls.js';
import { type Font, FontLoader } from 'src/lib/FontLoader.js';
import planConfig from 'src/config/plan';

export function initScene(canvas, animationLoop = null) {
	// Init Canvas
	canvas.width = window.innerWidth;
	canvas.height = window.innerHeight;

	// Init Scene
	const scene = new THREE.Scene();
	scene.background = new THREE.Color(planConfig.scene.backgroundColor);

	// Init Renderer
	const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
	renderer.shadowMap.enabled = true;
	renderer.setSize(window.innerWidth, window.innerHeight, false);

	if (animationLoop) {
		renderer.setAnimationLoop(animationLoop);
	}

	// Init Camera
	const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 1, 20000);
	camera.updateProjectionMatrix();

	let controls;
	if (planConfig.features.controls) {
		controls = new OrbitControls(camera, canvas);

		controls.enableDamping = true;
		controls.dampingFactor = 0.05;
		controls.screenSpacePanning = false;
		controls.minDistance = 0;
		controls.maxDistance = 20000;
		controls.update();
		// controls.maxPolarAngle = Math.PI / 2.4;
		controls.listenToKeyEvents(window);
	}

	// Init Lights
	const hemiLight = new THREE.HemisphereLight(0xffffff, 0x999999);
	hemiLight.position.set(0, 10, 0);
	scene.add(hemiLight);

	if (planConfig.features.plane) {
		const plane = new THREE.Mesh(
			new THREE.PlaneGeometry(2000, 2000),
			new THREE.MeshPhongMaterial({ color: 0x2d2d2d, depthWrite: false })
		);
		plane.rotation.x = -Math.PI / 2;
		plane.receiveShadow = true;
		scene.add(plane);
	}

	if (planConfig.features.grid) {
		const grid = new THREE.GridHelper(10000, 100, 0x000000, 0x000000);
		grid.material.opacity = 0.2;
		grid.material.transparent = true;
		scene.add(grid);
	}

	const context = {
		canvas,
		scene,
		renderer,
		camera,
		controls,
		font: null as Font,
	};

	const loader = new FontLoader();
	loader.load('/static/fonts/IRANSansX_Bold.json', function (font) {
		context.font = font;
	});

	return context;
}

export function centerCamera(x, y, context) {
	const { camera, controls, width, height } = context;

	const fov = (camera.fov * Math.PI) / 180;

	let cameraDist;
	if (camera.aspect > 1) {
		cameraDist = height / 2 / Math.tan(fov / 2);
	} else {
		cameraDist = width / 2 / Math.tan((fov * camera.aspect) / 2);
	}

	camera.position.set(x + width / 2, cameraDist, y + height / 2);
	camera.lookAt(x + width / 2, 0, y + height / 2);

	if (planConfig.features.controls) {
		controls.target = new THREE.Vector3(x + width / 2, 0, y + height / 2);
	}
}

export function calculateTargetCameraPosition(p1, p2, camera, renderer, opts: any = {}) {
	const { scalePadding = 1.3, pixelPadding = 100 } = opts;

	// Clone so we don’t mutate the real camera
	const cam = camera.clone();

	// Normalize inputs to XZ plane
	const a = new THREE.Vector3(p1.x, p1.z, p1.y);
	const b = new THREE.Vector3(p2.x, p2.z, p2.y);

	// Center of the two points
	const center = a.clone().add(b).multiplyScalar(0.5);

	// Required half-extents in world units on X and Z (height on screen corresponds to Z)
	let halfW = Math.abs(a.x - b.x) * 0.5;
	let halfH = Math.abs(a.z - b.z) * 0.5;

	// Scale padding first (world-space breathing room)
	halfW *= scalePadding;
	halfH *= scalePadding;

	// Camera params
	const fovRad = THREE.MathUtils.degToRad(cam.fov);
	const canvas = renderer.domElement;
	const aspect = canvas.clientWidth / canvas.clientHeight;

	// Convert pixel padding into extra world half-size at distance d:
	// worldHalfHeight(d) = d * tan(fov/2)
	// pixelHalfHeight    = canvas.clientHeight / 2
	// worldPerPixel(d)   = worldHalfHeight(d) / pixelHalfHeight = d * tan(fov/2) / (H/2)
	// We don’t know d yet, so we’ll include pixel padding *after* solving for d without it,
	// then resolve once more with the added world margin (one cheap extra step).

	// Step 1: distance to fit world extents (no pixel padding yet)
	const dByHeight = halfH / Math.tan(fovRad / 2);
	const dByWidth = halfW / aspect / Math.tan(fovRad / 2);
	let d = Math.max(dByHeight, dByWidth);

	// Step 2: add pixel padding as world margin at that distance and resolve distance again
	if (pixelPadding > 0) {
		const worldPerPixel = (d * Math.tan(fovRad / 2)) / (canvas.clientHeight * 0.5);
		const extraWorld = worldPerPixel * pixelPadding; // on each side
		halfH += extraWorld;
		halfW += extraWorld * aspect; // maintain roughly equal visual margin on sides
		const d2ByHeight = halfH / Math.tan(fovRad / 2);
		const d2ByWidth = halfW / aspect / Math.tan(fovRad / 2);
		d = Math.max(d2ByHeight, d2ByWidth);
	}

	// Place camera straight above center at Y = d
	const position = new THREE.Vector3(center.x, d, center.z);

	// Aim straight down with desired screen "up" direction
	cam.position.copy(position);
	cam.lookAt(center);

	return {
		position: cam.position.clone(),
		quaternion: cam.quaternion.clone(),
		distance: d,
	};
}
